import axios from 'axios'
import { Autocomplete, AutocompleteProps, MenuItem, TextField, TextFieldProps } from '@mui/material'
import { CatalogProduct, CatalogProductQuery } from '../types'
import { useEffect, useState } from 'react'
import { getCatalogProducts } from '../services/catalogProductService'
import { useTranslation } from 'react-i18next'

export interface CatalogProductProductCodeAutocompleteProps extends Omit<
  AutocompleteProps<string, false, false, true>,
  'options' | 'value' | 'renderInput' | 'groupBy'
> {
  query?: CatalogProductQuery
  value?: string | null
  label?: string
  placeholder?: string
  error?: TextFieldProps['error']
  helperText?: TextFieldProps['helperText']
}

export default function CatalogProductProductCodeAutocomplete({
  query = {},
  value,
  label,
  placeholder,
  error,
  helperText,
  inputValue,
  ...props
}: CatalogProductProductCodeAutocompleteProps) {
  const { t } = useTranslation()

  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([])
  const [hasLoadError, setHasLoadError] = useState(false)
  const { type, status, manufacturer, fuse, heatingPower } = query
  const hasHelperText =
    helperText !== undefined && helperText !== null && helperText !== false && helperText !== ''
  const resolvedError = Boolean(error) || (!hasHelperText && hasLoadError)
  const resolvedHelperText = hasHelperText
    ? helperText
    : hasLoadError
      ? t('actions:loadError')
      : helperText

  const sortedOptions = makeOptions(catalogProducts)

  function makeOptions(products: CatalogProduct[]) {
    return [
      ...new Set(
        products
          .filter((product) => (product.manufacturerProductCode ?? '').length > 0)
          .map((product) => product.manufacturerProductCode!)
      )
    ].sort((a, b) => a!.localeCompare(b!))
  }

  async function loadCatalogProducts(query: CatalogProductQuery, controller?: AbortController) {
    try {
      setHasLoadError(false)
      setCatalogProducts(await getCatalogProducts(query, { signal: controller?.signal }))
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }

      setHasLoadError(true)
      setCatalogProducts([])
    }
  }

  useEffect(() => {
    const controller = new AbortController()

    loadCatalogProducts(
      {
        type,
        status,
        manufacturer,
        fuse,
        heatingPower
      },
      controller
    )

    return () => controller.abort()
  }, [type, status, manufacturer, fuse, heatingPower])

  return (
    <Autocomplete
      options={sortedOptions}
      getOptionLabel={(option) => option}
      getOptionKey={(option) => option}
      noOptionsText={hasLoadError ? t('actions:loadError') : null}
      freeSolo={true}
      clearIcon={null}
      isOptionEqualToValue={(option, selectedValue) => option === selectedValue}
      renderOption={(props, option) => <MenuItem {...props}>{option}</MenuItem>}
      renderInput={(params) => (
        <TextField
          label={label ?? t('selectCatalogProduct')}
          placeholder={placeholder}
          value={inputValue ?? sortedOptions.find((option) => option === value) ?? ''}
          error={resolvedError}
          helperText={resolvedHelperText}
          {...params}
        />
      )}
      value={sortedOptions.find((option) => option === value) ?? null}
      {...props}
    />
  )
}
